import { styled } from '@mui/system';
import { Accordion, AccordionSummary, Box, Typography, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { COLORS } from './constants';
import { figmaLetterSpacing } from '../../ui/utils';
import { fontFamilies } from '../../constants/theme';

export const containerStyles = {
  maxWidth: '808px',
  margin: '0 auto',
  padding: 0,
};

export const StyledAccordion = styled(Accordion, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})(({ isOpen }) => ({
  borderRadius: '30px !important',
  background: isOpen ? COLORS.CARD_OPEN : COLORS.CARD,
  border: isOpen ? '1px solid' : '1px solid transparent',
  borderColor: isOpen ? 'black' : 'transparent',
  boxShadow:
    '-4px -4px 17.8px 0px rgba(205, 214, 223, 0.15), 4px 4px 15px 0px rgba(205, 214, 223, 0.25)',
  '&:before': { display: 'none' },
  gap: '22.5px',
}));

export const StyledIconButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})(({ isOpen }) => ({
  width: 50,
  height: 50,
  marginRight: 0,
  borderRadius: '20px',
  background: isOpen ? COLORS.DOT : '#fff',
  color: isOpen ? '#fff' : COLORS.DOT,
  border: '1px solid',
  borderColor: isOpen ? 'transparent' : COLORS.STROKE,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
}));

export const HeadingText = styled(Typography)(({ theme }) => ({
  ...theme.typography.heading,
  fontSize: '32px',
  fontFamily: 'Gotham',
  fontWeight: 400,
  letterSpacing: figmaLetterSpacing(50, -1),
  textAlign: 'center',

  [theme.breakpoints.up('sm')]: {
    fontSize: '40px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '50px',
    lineHeight: '58px',
  },
}));

export const QuestionText = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtext,
  margin: 0,
  fontFamily: 'Quicksand',
  fontWeight: 700,
  textAlign: 'left',
  fontSize: '20px',
  letterSpacing: figmaLetterSpacing(20, 2),
  flex: 1,
  [theme.breakpoints.down('md')]: {
    fontSize: '18px',
    lineHeight: '24px',
    letterSpacing: figmaLetterSpacing(18, 2),
    fontWeight: 600,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
  },
}));

export const AnswerText = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtext,
  fontSize: '14px',
  textAlign: 'left',
  fontFamily: 'Quicksand',
  fontWeight: 400,
  fontStyle: 'Regular',
  letterSpacing: figmaLetterSpacing(16, 2),
  whiteSpace: 'pre-line',
  maxWidth: '90%',
  [theme.breakpoints.up('md')]: {
    fontSize: '16px',
    lineHeight: '20px',
  },
}));

export const AnswerList = styled('ul')(({ theme }) => ({
  margin: 0,
  paddingLeft: 0,
  listStyle: 'none',
  fontSize: '14px',
  fontFamily: 'Quicksand',
  fontWeight: 400,
  letterSpacing: figmaLetterSpacing(16, 2),
  color: COLORS.TEXT,
  '& li': {
    paddingLeft: '0.8em',
    textIndent: '-0.8em',
    marginBottom: '0.5em',
    '&:last-child': {
      marginBottom: 0,
    },
    '&::before': {
      content: '"• "',
      fontWeight: 'bold',
    },
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '16px',
    lineHeight: '20px',
  },
}));

export const AccordionSummaryStyled = styled(AccordionSummary, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})(({ isOpen }) => ({
  padding: isOpen ? '20px 20px 0 30px' : '20px 20px 20px 30px',
  '& .MuiAccordionSummary-content': {
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  background: 'transparent',
}));

export const accordionDetailsStyles = {
  padding: '10px 20px 29px 30px',
  background: 'transparent',
};

export const CategoryChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  borderRadius: theme.spacing(2.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(2.5),
  paddingBottom: theme.spacing(2.5),
  height: 'auto',
  fontFamily: fontFamilies.gotham,
  fontWeight: 500,
  color: COLORS.TEXT,
  background: isActive ? '#F3EEE7' : 'transparent',
  border: isActive ? '1px solid' : '1px solid transparent',
  borderColor: isActive ? 'black' : COLORS.PILL_BORDER,
  transition: 'all 0.2s ease',
  boxShadow: 'none',
  fontSize: '18px',
  '&:hover': {
    background: isActive ? '#F7F3F1' : '#FAFAFA',
    boxShadow: 'none',
  },
  '&:active': {
    boxShadow: 'none',
  },
  '&:focus': {
    boxShadow: 'none',
  },
  '& .MuiTouchRipple-root': {
    display: 'none',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: 16,
  },
}));

export const AddIconStyled = styled(AddIcon)({
  fontSize: 18,
});
